<template>
  <div>
    <div class="page-header">
      <h1>Página de Tracking DHL</h1>
      <p>Carregar e visualizar dados de encomendas da DHL.</p>
    </div>

    <!-- Top bar with Upload and File Selector centered -->
    <div class="top-bar">
      <!-- Upload area -->
      <div class="upload-area">
        <form @submit.prevent="uploadFile" class="upload-form">
          <label for="file">Enviar ficheiro Excel:</label>
          <input
            ref="fileInput"
            type="file"
            id="file"
            accept=".xlsx, .xls"
            @change="handleFileChange"
          />
          <button type="submit" :disabled="!selectedFile">Upload</button>
        </form>
        <p v-if="uploadError" class="error">{{ uploadError }}</p>
        <p v-if="uploadSuccess" class="success">Upload concluído!</p>
      </div>

      <!-- File selector dropdown -->
      <div class="file-selector" v-if="files.length">
        <label for="fileSelect">Ficheiro:</label>
        <select id="fileSelect" v-model="current_filename">
          <option
            v-for="file in files"
            :key="file.fileName"
            :value="file.fileName"
          >
            {{ file.fileName }}
          </option>
        </select>
      </div>
    </div>

    <div class="orders-table-wrapper">
      <table class="orders-table">
        <thead>
          <tr>
            <th v-for="(header, idx) in tableHeaders" :key="idx">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(order, rowIdx) in current_orders" :key="rowIdx">
            <td v-for="(header, colIdx) in tableHeaders" :key="colIdx">
              {{ order[header] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex/dist/vuex.common.js';

export default {
  data() {
    return {
      current_orders: [],
      current_filename: '',
      files: [],
      selectedFile: null,
      uploadError: '',
      uploadSuccess: false,
    };
  },
  methods: {
    handleFileChange(e) {
      this.uploadError = '';
      this.uploadSuccess = false;
      this.selectedFile = e.target.files[0] || null;
    },
    async uploadFile() {
      if (!this.selectedFile) return;
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      try {
        const response = await fetch(
          `${this.$store.state.apiUrl}dhlShebin/upload-xlsx`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${this.$store.state.token}` },
            body: formData,
          }
        );
        if (response.ok) {
          this.uploadSuccess = true;
          this.uploadError = '';
          this.selectedFile = null;
          // reset file input so it shows "no file chosen"
          this.$refs.fileInput.value = '';
          await this.getOrders();
        } else {
          const errorData = await response.json();
          this.uploadError =
            errorData.message || 'Erro ao fazer upload do ficheiro.';
        }
      } catch (err) {
        console.error('Error uploading file:', err);
        this.uploadSuccess = false;
      }
    },
    async getOrders() {
      try {
        const payload = {
          path: './files/dhl-shebin',
          columns: [
            'Invoice Number',
            'Invoice Identifier',
            'Invoice Date',
            'Shipment Number',
            'Shipment Date',
            'Shipment Reference 1',
            'Cust Scale Weight (A)',
            'DHL Scale Weight (B)',
            'Cust Vol Weight (V)',
            'DHL Vol Weight (W)',
            'Weight Flag',
            'Weight (kg)',
            'Total amount (incl. VAT)',
            'Weight Charge',
            'Total Extra Charges (XC)',
            'XC1 Name',
            'XC1 Total',
            'XC2 Name',
            'XC2 Total',
          ],
        };
        await this.$store.dispatch('getShebinDhlOrders', payload);
        this.files = this.getShebinDHL;
        this.current_filename = this.files[0].fileName;
        this.current_orders = this.files[0].rows;
      } catch (error) {
        console.error('Error fetching orders:', error);
      }
    },
  },
  computed: {
    ...mapGetters(['getShebinDHL']),
    tableHeaders() {
      return this.current_orders.length
        ? Object.keys(this.current_orders[0])
        : [];
    },
  },
  watch: {
    current_filename(newName) {
      const file = this.files.find((f) => f.fileName === newName);
      this.current_orders = file ? file.rows : [];
    },
  },
  async created() {
    await this.getOrders();
  },
};
</script>

<style scoped>
:root {
  --accent-pink: #ff6f6f;
  --accent-light: #ffecec;
  --text-dark: #333;
}

.page-header {
  text-align: center;
  margin-bottom: 1.5em;
}
.page-header h1 {
  margin: 0;
  font-size: 2em;
  color: var(--text-dark);
}
.page-header p {
  margin: 0.5em 0 0;
  color: var(--text-dark);
}

.top-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1em;
  margin-bottom: 1.5em;
}

.upload-area,
.file-selector {
  background: var(--accent-light);
  border: 1px solid var(--accent-pink);
  padding: 1em;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}

.upload-area form {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.upload-area input[type='file'] {
  padding: 0.4em 0.6em;
  border: 1px solid var(--accent-pink);
  border-radius: 4px;
}

.upload-area button {
  padding: 0.4em 1em;
  background: #ff9393;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.upload-area button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.file-selector label {
  margin-bottom: 0.5em;
}

.file-selector select {
  padding: 0.4em 0.6em;
  border: 1px solid var(--accent-pink);
  border-radius: 4px;
}

.orders-table-wrapper {
  overflow-x: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 6px;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;
}

.orders-table th,
.orders-table td {
  border: 1px solid #ddd;
  padding: 8px;
  white-space: nowrap;
}

.orders-table th {
  background-color: var(--accent-pink);
  color: #000;
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
}

.orders-table tr:nth-child(even) {
  background-color: #fafafa;
}

.orders-table tr:hover {
  background-color: #f0f0f0;
}

.error {
  color: #c00;
  margin-top: 0.5em;
}

.success {
  color: #080;
  margin-top: 0.5em;
}
</style>
